'use client';

// ---------------------------------------------------------------------------
// PlaylistsTab — create playlists, save the queue into one, play one
// ---------------------------------------------------------------------------
//
// "Save the current queue as a playlist" is the primary way to build one. The
// alternative — a separate drag-and-drop builder — duplicates the queue editor
// that already exists one tab away, and a manager assembling a playlist is doing
// exactly what the queue is for.
//
// Local entries are saved with the rest and labelled. They resolve only on the
// device holding the file, so the dialog says how many will not travel rather
// than dropping them: a playlist that silently differs from what was just built
// is worse than one with a visible caveat.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { AlertTriangle, ListPlus, Play, Save, Trash2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { useTranslation } from '@/core/i18n';
import { useToast } from '@/core/hooks/useToast';
import { cn } from '@/core/utils/cn';

import { useMusicPlaylists, type Playlist } from '../hooks/useMusicPlaylists';
import { countDeviceOnly, isLocalRef, toPlaylistItemInputs } from '../lib/track';
import { selectQueueTracks, useMusicStore } from '../stores/music.store';

export function PlaylistsTab() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const {
    playlists,
    isLoading,
    unavailable,
    createPlaylist,
    deletePlaylist,
    messageOf,
  } = useMusicPlaylists();

  // useShallow: the selector returns a new array each call — see the note in
  // MusicExpandedPanel. Without it this tab re-renders until React aborts.
  const queueTracks = useMusicStore(useShallow(selectQueueTracks));
  const playNow = useMusicStore((s) => s.playNow);

  const [name, setName] = useState('');

  const deviceOnlyCount = countDeviceOnly(queueTracks);

  const saveQueue = async () => {
    if (!name.trim() || queueTracks.length === 0) return;
    try {
      await createPlaylist.mutateAsync({
        name: name.trim(),
        items: toPlaylistItemInputs(queueTracks),
      });
      toast('success', t('music.title'), t('music.playlistSaved', { name: name.trim() }));
      setName('');
    } catch (err) {
      // The server's message is the useful one: a taken name, or a track that was
      // hidden while the queue was open.
      toast('error', t('music.title'), messageOf(err, t('music.playlistSaveFailed')));
    }
  };

  /**
   * Play a saved playlist.
   *
   * Entries whose track was hidden by an admin come back with `track: null`, and
   * are skipped here — the alternative is a queue with holes that stall.
   */
  const play = (playlist: Playlist) => {
    const ids = [...playlist.items]
      .sort((a, b) => a.position - b.position)
      .map((i) => (i.trackId && i.track ? i.trackId : i.localRef))
      .filter((id): id is string => Boolean(id));

    if (ids.length === 0) {
      toast('info', t('music.title'), t('music.playlistEmpty'));
      return;
    }
    playNow(ids);
    useMusicStore.getState().play();
  };

  if (unavailable) {
    // A platform or chain admin has no property in scope. Saying so beats
    // showing an editor that cannot save.
    return <p className="px-1 text-[12px] text-hms-textDim">{t('music.playlistsNeedProperty')}</p>;
  }

  return (
    <div className="space-y-3">
      <section className="px-1">
        <p className="text-[10px] font-semibold text-hms-textDim uppercase tracking-wider pb-1">
          {t('music.saveQueueAs')}
        </p>
        <div className="flex items-center gap-1.5">
          <input
            id="music-playlist-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('music.playlistNamePlaceholder')}
            className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg bg-hms-bg border border-hms-border text-[12.5px] text-hms-text placeholder:text-hms-textDim"
          />
          <button
            id="music-playlist-save"
            onClick={() => void saveQueue()}
            disabled={!name.trim() || queueTracks.length === 0 || createPlaylist.isPending}
            aria-label={t('music.savePlaylist')}
            title={t('music.savePlaylist')}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-hms-teal/15 text-hms-teal hover:bg-hms-teal/25 disabled:opacity-30 flex-shrink-0 transition-all duration-200"
          >
            <Save size={14} strokeWidth={2} />
          </button>
        </div>

        {queueTracks.length === 0 ? (
          <p className="text-[10.5px] text-hms-textDim mt-1">{t('music.queueEmptyToSave')}</p>
        ) : (
          <p className="text-[10.5px] text-hms-textDim mt-1">
            {t('music.willSaveCount', { count: String(queueTracks.length) })}
            {deviceOnlyCount > 0 && (
              <>
                {' '}
                <span className="text-hms-gold">
                  {t('music.deviceOnlyWarning', { count: String(deviceOnlyCount) })}
                </span>
              </>
            )}
          </p>
        )}
      </section>

      <section>
        <p className="px-1 pb-1 text-[10px] font-semibold text-hms-textDim uppercase tracking-wider">
          {t('music.savedPlaylists')}
        </p>

        {isLoading ? (
          <p className="px-1 text-[12px] text-hms-textDim">{t('music.loading')}</p>
        ) : playlists.length === 0 ? (
          <p className="px-1 text-[12px] text-hms-textDim">{t('music.noPlaylists')}</p>
        ) : (
          <ul id="music-playlists-list">
            {playlists.map((playlist) => {
              const localCount = playlist.items.filter((i) => i.localRef && isLocalRef(i.localRef))
                .length;
              const hiddenCount = playlist.items.filter((i) => i.trackId && !i.track).length;

              return (
                <li
                  key={playlist.id}
                  className="group flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-white/[0.04] transition-all duration-200"
                >
                  <button
                    onClick={() => play(playlist)}
                    aria-label={t('music.playPlaylist', { name: playlist.name })}
                    className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-teal flex-shrink-0 transition-all duration-200"
                  >
                    <Play size={13} strokeWidth={2} />
                  </button>

                  <div className="flex-1 min-w-0">
                    <p dir="auto" className="text-[12.5px] text-hms-text truncate">
                      {playlist.name}
                    </p>
                    <span className="text-[10.5px] text-hms-textDim">
                      {t('music.trackCount', { count: String(playlist.items.length) })}
                      {localCount > 0 && ` · ${t('music.nDeviceOnly', { count: String(localCount) })}`}
                      {hiddenCount > 0 && (
                        <span className="text-hms-gold">
                          {' '}
                          · {t('music.nUnavailable', { count: String(hiddenCount) })}
                        </span>
                      )}
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      void deletePlaylist
                        .mutateAsync(playlist.id)
                        .then(() => toast('success', t('music.title'), t('music.playlistDeleted')))
                        .catch((err) =>
                          toast('error', t('music.title'), messageOf(err, t('music.deleteFailed'))),
                        );
                    }}
                    aria-label={t('music.deletePlaylist')}
                    title={t('music.deletePlaylist')}
                    className="flex items-center justify-center w-7 h-7 rounded-lg text-hms-textSub hover:text-hms-red opacity-0 group-hover:opacity-100 focus:opacity-100 flex-shrink-0 transition-all duration-200"
                  >
                    <Trash2 size={13} strokeWidth={2} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {playlists.some((p) => p.items.some((i) => i.trackId && !i.track)) && (
        <p className="flex items-start gap-1.5 px-1 text-[10.5px] text-hms-gold">
          <AlertTriangle size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
          {t('music.hiddenTrackNote')}
        </p>
      )}

      <p className="flex items-start gap-1.5 px-1 text-[10.5px] text-hms-textDim">
        <ListPlus size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
        {t('music.playlistBuildHint')}
      </p>
    </div>
  );
}
